(function() {

let DEBUG_MODE = true;
let log = DEBUG_MODE ? console.log : () => {};

// Mini-cart専用セレクタ
const CART_CONTAINER_SELECTOR = 'ul.mini-cart__navigation';
const ITEM_SELECTOR = 'li';
const INPUT_SELECTOR = 'input.quantity__input'; 
const IMAGE_CONTAINER_SELECTOR = '.product-image.media-wrapper .media';

const SPIXN_GREEN_FOR_MARK = '#17A089';
const SPIXN_BASE_COLOR = '#4A2D57';
const SPIXN_BUTTON_ACTIVE_BACKGROUND = 'linear-gradient(73deg,rgba(2, 188, 172, 1) 0%, rgba(75, 73, 101, 1) 100%)';
const SPIXN_GREEN = '#158E7A';
const SPIXN_LIGHT_GREEN = '#E8F7F5';

// ヘルパー関数：rem値をスケールされたcalc()に変換（オリジナルと同じ）
const SPIXN_BASE_FONT_SIZE = 24; // mini-cart用に少し小さく
const FONT_SIZE_RATIO = SPIXN_BASE_FONT_SIZE / 16;
function calcSize(size) {
	return `calc(${size} * ${FONT_SIZE_RATIO})`;
}

// mini-cart専用のvariant ID取得関数
const getVariantIdFromItem = (item) => {
	return parseInt(item.getAttribute('data-variant-id'), 10);
};

// === API関連の設定と関数 ===
let API_BASE_URL = 'https://ieyxoq7t4c.execute-api.ap-northeast-1.amazonaws.com/dev';
let SHOP_ID = null;
const buybackCache = new Map();

function getShopId() { return SHOP_ID; }
function setShopId(shopId) { SHOP_ID = shopId; }

function validateShopId(shopId) {
	if (!shopId || typeof shopId !== 'string') return false;
	return /^[a-zA-Z0-9\-]+\.myshopify\.com$/.test(shopId);
}

async function callSpixnAPI(endpoint, params = {}) {
	try {
		const url = new URL(`${API_BASE_URL}${endpoint}`);
		Object.keys(params).forEach(key => {
			if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
				url.searchParams.append(key, params[key]);
			}
		});

		const response = await fetch(url.toString(), {
			method: 'GET',
			headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' }
		});

		if (!response.ok) throw new Error(`API call failed: ${response.status}`);
		return await response.json();
	} catch (error) {
		throw error;
	}
}

async function getBuybackInformation(variantId) {
	try {
		const shopId = getShopId();
		if (!shopId) return null;

		const cacheKey = `${shopId}_${variantId}`;
		if (buybackCache.has(cacheKey)) {
			const cached = buybackCache.get(cacheKey);
			if (Date.now() - cached.timestamp < 300000) {
				return cached.data;
			}
		}

		const params = { sp_shop_id: shopId, sp_variant_id: variantId.toString() };
		const response = await callSpixnAPI('/shopify/buyback', params);

		if (!response.data || !Array.isArray(response.data) || response.data.length === 0) {
			buybackCache.set(cacheKey, { data: null, timestamp: Date.now() });
			return null;
		}

		const buybackData = response.data[0];
		const transformedData = {
			buyback_variant_id: parseInt(buybackData.buyback_sp_variant_id),
			insurance_fee: buybackData?.insurance_fee || 0,
			insurance_period_days: buybackData.insurance_period_days,
			a_price: buybackData.a_price,
			b_price: buybackData.b_price,
			c_price: buybackData.c_price
		};

		buybackCache.set(cacheKey, { data: transformedData, timestamp: Date.now() });
		return transformedData;
	} catch (error) {
		console.error('[SPIXN Mini-Cart] Failed to get buyback information:', error);
		return null;
	}
}

/**
 * バイバック保証商品リストをAPIから取得
 */
async function getBuybackProductList(productIds) {
	try {
		const shopId = getShopId();
		if (!shopId) {
			log('[SPIXN Mini-Cart] Shop ID not set, skipping buyback product list retrieval');
			return [];
		}

		if (!productIds || productIds.length === 0) {
			log('[SPIXN Mini-Cart] No product IDs provided for buyback product list retrieval');
			return [];
		}

		// APIパラメータを構築
		const params = {
			sp_shop_id: shopId,
			product_ids: productIds.join(',')
		};

		log('[SPIXN Mini-Cart] Calling buyback product list API with params:', params);

		// API呼び出し
		const response = await callSpixnAPI('/shopify/buyback/products', params);

		// レスポンス検証
		if (!response.data || !response.data.buyback_product_ids) {
			log('[SPIXN Mini-Cart] No buyback product list found:', response);
			return [];
		}

		const buybackProductIds = response.data.buyback_product_ids.map(id => parseInt(id, 10));
		log('[SPIXN Mini-Cart] Buyback product list retrieved:', buybackProductIds);

		return buybackProductIds;
	} catch (error) {
		console.error('[SPIXN Mini-Cart] Failed to get buyback product list:', error);
		return [];
	}
}

function debounce(func, wait) {
	let timeout;
	return function executedFunction(...args) {
		clearTimeout(timeout);
		timeout = setTimeout(() => func(...args), wait);
	};
}

class BuybackMiniCartManager {
	constructor() {
		if (window.__SPIXN_BUYBACK_MINI_CART_MANAGER__) {
			return window.__SPIXN_BUYBACK_MINI_CART_MANAGER__;
		}
		
		this.cartState = new Map();
		this.buybackRelations = new Map();
		this.isProcessing = false;
		this.debouncedSync = debounce(this.syncBuybackRelationships.bind(this), 100);
		this.reorderingEnabled = true;
		this.domReorderApplied = new Set();
		
		window.__SPIXN_BUYBACK_MINI_CART_MANAGER__ = this;
		this.init();
	}

	async init() {
		this.setupCartObserver();
		
		// 統合バリデーション・削除機能を追加
		this.lastAlertState = new Map();
		this.periodicValidationInterval = null;
		this.pendingDeletions = new Set();
		this.setupUnifiedValidation();
		
		await this.loadInitialCartState();
		log('✅ Mini-Cart版初期化完了（孤立商品削除機能付き）');
	}

	setupCartObserver() {
		if (!window.PerformanceObserver) return;

		const cartObserver = new PerformanceObserver((list) => {
			list.getEntries().forEach((entry) => {
				if (['xmlhttprequest', 'fetch'].includes(entry.initiatorType) && /\/cart\//.test(entry.name)) {
					this.handleCartChangeDetected();
				}
			});
		});

		cartObserver.observe({ entryTypes: ["resource"] });
	}

	handleCartChangeDetected() {
		setTimeout(() => { this.debouncedSync(); }, 100);
	}

	async syncBuybackRelationships() {
		if (this.isProcessing) return;
		this.isProcessing = true;
		
		try {
			const cartData = await this.fetchCartState();
			this.updateCartState(cartData);
			await this.buildBuybackRelations();
			this.reorderCartItemsWithDOMMove();
			this.addBuybackInfoToAllContainers();
		} catch (error) {
			console.error('❌ Error during sync:', error);
		} finally {
			this.isProcessing = false;
		}
	}

	async loadInitialCartState() {
		try {
			const cartData = await this.fetchCartState();
			this.updateCartState(cartData);
			await this.buildBuybackRelations();
			this.reorderCartItemsWithDOMMove();
			this.addBuybackInfoToAllContainers();
			
			// 初期状態での統合チェック実行（サイレントモード）
			await this.performUnifiedValidationAndCleanup(true);
			
			log('✅ Initial cart state loaded with orphan product check');
		} catch (error) {
			console.error('❌ Failed to load initial cart state:', error);
		}
	}

	async fetchCartState() {
		const response = await fetch(window.Shopify.routes.root + 'cart.js');
		if (!response.ok) throw new Error('Failed to fetch cart state');
		return response.json();
	}

	updateCartState(cartData) {
		this.cartState.clear();
		cartData.items.forEach(item => {
			this.cartState.set(item.variant_id, {
				quantity: item.quantity,
				product_id: item.product_id
			});
		});
	}

	async buildBuybackRelations() {
		this.buybackRelations.clear();
		this.domReorderApplied.clear();
		
		for (const [variantId] of this.cartState) {
			await this.processBuybackRelationForVariant(variantId);
		}
	}

	async processBuybackRelationForVariant(variantId) {
		try {
			const buybackData = await getBuybackInformation(variantId);
			
			if (buybackData) {
				const buybackVariantId = buybackData.buyback_variant_id;
				
				this.buybackRelations.set(variantId, {
					type: 'main',
					buybackVariantId: buybackVariantId,
					mainVariantId: variantId
				});
				
				if (this.cartState.has(buybackVariantId)) {
					this.buybackRelations.set(buybackVariantId, {
						type: 'buyback',
						buybackVariantId: buybackVariantId,
						mainVariantId: variantId
					});
				}
			}
		} catch (error) {
			// エラーは無視して続行
		}
	}

	reorderCartItemsWithDOMMove() {
		const cartContainers = Array.from(document.querySelectorAll(CART_CONTAINER_SELECTOR));
		
		for (const cartContainer of cartContainers) {
			const containerId = this.getContainerId(cartContainer);
			if (this.domReorderApplied.has(containerId)) continue;
			
			const pairs = this.findBuybackPairsInContainer(cartContainer);
			if (pairs.length === 0) continue;
			
			pairs.forEach(pair => this.reorderBuybackPair(pair));
			this.domReorderApplied.add(containerId);
		}
	}

	reorderBuybackPair(pair) {
		const mainRect = pair.main.getBoundingClientRect();
		const buybackRect = pair.buyback.getBoundingClientRect();
		
		if (buybackRect.top < mainRect.top) {
			pair.buyback.parentNode.insertBefore(pair.main, pair.buyback);
		}
	}

	getContainerId(container) {
		const rect = container.getBoundingClientRect();
		return `container_${Math.round(rect.top)}_${Math.round(rect.left)}`;
	}

	findBuybackPairsInContainer(container) {
		const pairs = [];
		const processedItems = new Set();
		const cartItems = Array.from(container.querySelectorAll(ITEM_SELECTOR));
		
		for (const item of cartItems) {
			if (processedItems.has(item)) continue;
			
			const variantId = getVariantIdFromItem(item);
			const relation = this.buybackRelations.get(variantId);
			if (!relation || relation.type !== 'main') continue;
			
			const buybackVariantId = relation.buybackVariantId;
			const buybackItem = cartItems.find(candidateItem => {
				if (processedItems.has(candidateItem)) return false;
				return getVariantIdFromItem(candidateItem) === buybackVariantId;
			});
			
			if (buybackItem) {
				pairs.push({ main: item, buyback: buybackItem });
				processedItems.add(item);
				processedItems.add(buybackItem);
			}
		}
		
		return pairs;
	}

	addBuybackInfoToAllContainers() {
		const cartContainers = Array.from(document.querySelectorAll(CART_CONTAINER_SELECTOR));
		for (const cartContainer of cartContainers) {
			this.addBuybackInfoToCartItems(cartContainer);
		}
	}

	addBuybackInfoToCartItems(container) {
		for (const [variantId, relation] of this.buybackRelations) {
			if (relation.type === 'buyback') {
				this.addBuybackInfoToCartItem(container, variantId, relation);
			}
		}
	}

	async addBuybackInfoToCartItem(container, buybackVariantId, relation) {
		const cartItem = this.findCartItemByVariantId(container, buybackVariantId);
		if (!cartItem || cartItem.querySelector('.spixn-buyback-info-part')) return;

		const buybackData = await getBuybackInformation(relation.mainVariantId);
		if (!buybackData) return;

		this.addBuybackImageOverlay(cartItem, relation.mainVariantId);
		const buybackInfoPart = this.createBuybackInfoPart(buybackData);
		
		// const productDescription = cartItem.querySelector('.product-description');
		// if (productDescription) {
		// 	productDescription.appendChild(buybackInfoPart);
		// } else {
		// 	cartItem.appendChild(buybackInfoPart);
		// }
		cartItem.appendChild(buybackInfoPart);

		// this.addBuybackInstruction(cartItem, productDescription);
		this.addBuybackInstruction(cartItem);
	}

	findCartItemByVariantId(container, variantId) {
		return container.querySelector(`${ITEM_SELECTOR}[data-variant-id="${variantId}"]`);
	}

	addBuybackImageOverlay(cartItem, mainVariantId) {
		log('🖼️ addBuybackImageOverlay called');
		
		const productContainer = cartItem.querySelector('.product-container');
		if (!productContainer) {
			log('❌ .product-container not found');
			return;
		}
		
		// media-wrapperを検索、なければ作成
		let mediaWrapper = productContainer.querySelector('.product-image.media-wrapper');
		if (!mediaWrapper) {
			log('📦 Creating media-wrapper');
			mediaWrapper = document.createElement('div');
			mediaWrapper.className = 'product-image media-wrapper media-wrapper--small';
			productContainer.insertBefore(mediaWrapper, productContainer.firstChild);
			productContainer.classList.remove('no-image');
		}
		
		// .mediaを検索、なければ作成
		let imageContainer = mediaWrapper.querySelector('.media');
		if (!imageContainer) {
			log('📦 Creating .media container');
			imageContainer = document.createElement('div');
			imageContainer.className = 'media media--adapt';
			mediaWrapper.appendChild(imageContainer);
		}
		
		if (imageContainer.querySelector('.spixn-buyback-image-wrapper')) {
			log('⚠️ Overlay already exists');
			return;
		}
		
		if (mainVariantId) {
			this.replaceWithMainProductImage(imageContainer, mainVariantId);
		}
		
		imageContainer.style.position = 'relative';
		const overlay = this.createImageWrapper();
		imageContainer.appendChild(overlay);
		log('✅ Overlay added');
	}

	replaceWithMainProductImage(buybackImageContainer, mainVariantId) {
		try {
			const mainCartItem = this.findCartItemByVariantId(
				buybackImageContainer.closest(CART_CONTAINER_SELECTOR),
				mainVariantId
			);
			
			if (!mainCartItem) return;
			
			const mainImageContainer = mainCartItem.querySelector(IMAGE_CONTAINER_SELECTOR);
			if (!mainImageContainer) return;
			
			const mainImg = mainImageContainer.querySelector('img');
			if (!mainImg) return;
			
			const clonedImg = mainImg.cloneNode(true);
			const buybackImg = buybackImageContainer.querySelector('img');
			
			if (buybackImg) {
				buybackImg.replaceWith(clonedImg);
			} else {
				buybackImageContainer.appendChild(clonedImg);
			}
			
			log('✅ Image setup completed');
		} catch (error) {
			console.error('❌ Error:', error);
		}
	}

	createImageWrapper() {
		const imgWrapper = document.createElement('div');
		imgWrapper.className = 'spixn-buyback-image-wrapper';
		imgWrapper.style.cssText = `
			width: 100%;
			height: 100%;
			display: flex; 
			flex-direction: column; 
			gap: ${calcSize('0.3rem')}; 
			align-items: center; 
			justify-content: center; 
			position: absolute; 
			top: 0;
			left: 0; 
			z-index: 1; 
			background-color: rgba(230, 248, 247, 0.7); 
			color: ${SPIXN_BASE_COLOR}; 
			font-weight: 700; 
			font-size: ${calcSize('0.4rem')}; 
			word-break: keep-all; 
			border: 4px solid;
			border-image:  ${SPIXN_BUTTON_ACTIVE_BACKGROUND} 1;
			box-sizing: border-box;
		`;
		imgWrapper.innerHTML = `
		  <div style="text-align: center;">
		    <div>
			${this.createSpixnMarkSVG(12)}
			</div>
		  	<div style="font-size: ${calcSize('0.52rem')};">バイバック保証</div>
		  	<div style="font-size: ${calcSize('0.25rem')}; display: none;">
			  購入後 spixnの<br />
			  会員登録が必須
			</div>
		  </div>`;
		return imgWrapper;
	}

	addBuybackInstruction(cartItem){
		const instruction = document.createElement('p');
		instruction.innerHTML = `
		【※保証を使うにはspixn会員登録が必要です】<br />
		購入後に届くメール、または <a target="_blank" style="color:inherit" href="https://user.spixn.co.jp">${this.createSpixnMarkCircleSVG()}spixnサイト</a>から会員登録してください。
		購入時と同じメールアドレスで登録すると保証が利用できます。
		`;
		instruction.style.cssText = `
			color: ${SPIXN_BASE_COLOR}; 
			text-align: left; 
			padding: 0 ${calcSize('6px')}; 
			font-size: ${calcSize('0.85rem')}; 
			margin: ${calcSize('0.5rem')} 0;
			max-width: 800px;
			min-width: 22rem;
			line-height: normal;
		`;
		
		cartItem.appendChild(instruction);
		// if (productDescription) {
		// 	productDescription.appendChild(instruction);
		// } else {
		// 	cartItem.appendChild(instruction);
		// }
	}

	createBuybackInfoPart(buybackData) {
		const ACCORDION_MAX_HEIGHT = '600px';
		const BUYBACK_MODAL_SHOW_PRICE_RANGE = false;
		const SHOW_BUYBACK_PRICE_AS_PERCENTAGE = false;

		// 既存の形式に変換
		const buybackInfo = {
			buyback_variant_id: buybackData.buyback_variant_id,
			insurance_period_days: buybackData.insurance_period_days,
			plans: [{
				plan: 'default',
				insurance_fee: buybackData.insurance_fee,
				insurance_period_days: buybackData.insurance_period_days,
				a_price: buybackData.a_price,
				b_price: buybackData.b_price,
				c_price: buybackData.c_price,
				a_min_price: Math.round(buybackData.a_price * 0.85),
				a_max_price: Math.round(buybackData.a_price * 1.15),
				b_min_price: Math.round(buybackData.b_price * 0.85),
				b_max_price: Math.round(buybackData.b_price * 1.15),
				c_min_price: Math.round(buybackData.c_price * 0.85),
				c_max_price: Math.round(buybackData.c_price * 1.15),
				a_price_ratio: buybackData.a_price / 10000, // 仮の比率
				b_price_ratio: buybackData.b_price / 10000,
				c_price_ratio: buybackData.c_price / 10000
			}]
		};

		const plan = buybackInfo.plans[0];
		const insurancePeriodYear = plan.insurance_period_days / 365;
		
		const blockStyle = `
			border-radius: 12px; 
			padding: 4px;
			margin: ${calcSize('20px')} 0 ${calcSize('1rem')}; 
			background: ${SPIXN_BUTTON_ACTIVE_BACKGROUND};
			transition: max-height 0.3s ease-in-out, padding 0.3s ease-in-out, margin 0.3s ease-in-out, border 0.3s ease-in-out; 
			color: ${SPIXN_BASE_COLOR};
			min-width: 220px;
			max-width: 500px;
			overflow: hidden;
		`;
		const contentStyle = `
		  padding: ${calcSize('1rem')}; 
		  border-radius: 9px;
		  background-color: ${SPIXN_LIGHT_GREEN};
		`;
		
		const priceRangeTitleHTML = `
			<div style="display: flex; flex-direction: column; justify-content: end; align-items: end; margin-bottom: ${calcSize('0.8rem')}; font-size: ${calcSize('0.7rem')};font-weight: bold;">
				<span style="color: ${SPIXN_BASE_COLOR};">紫色: バイバック保証額</span>
				<span style="color: #A3A3A3;">グレー: 2次流通品の主要取引価格帯（spixn調査）</span>
			</div>
		`;
		
		const showPriceAsPercentageConfig = SHOW_BUYBACK_PRICE_AS_PERCENTAGE;
		const showPriceRangeConfig = BUYBACK_MODAL_SHOW_PRICE_RANGE;

		const maxPriceToDisplay = showPriceAsPercentageConfig ? Math.round(plan.a_price_ratio * 100) : plan.a_price.toLocaleString();
		const priceUnit = showPriceAsPercentageConfig ? '%' : '';
		const pricePrefix = showPriceAsPercentageConfig ? '' : '￥';
		const insuranceFee = plan.insurance_fee.toLocaleString();

		const part = document.createElement('div');
		part.className = 'spixn-buyback-info-part';
		part.style.cssText = `
			padding: 0 0 0 ${calcSize('6px')};
			display: table;
			border: none;
			max-width: 60rem;
		`;
		part.innerHTML = `
			<div class='spixn-buyback-info-part-title' style="display: flex; flex-wrap: wrap; align-items: baseline;color: ${SPIXN_BASE_COLOR};font-weight: 700;justify-content:start;margin-bottom: ${calcSize('5px')};cursor: pointer;font-size: ${calcSize('0.9rem')};">
				<span style="color: #F3004D;">手数料</span><span style="color: #F3004D;font-size: ${calcSize('0.6rem')}">￥</span><span style="color: #F3004D;font-size: ${calcSize('1rem')};">${insuranceFee}</span>
				<span>で最大 ${pricePrefix}</span><span style="font-size: ${calcSize('1rem')};">${maxPriceToDisplay}</span><span>${priceUnit}のバイバック保証適用</span>
				${this.createChevronIcon()}
			</div>
			<div class="spixn-buyback-modal-core" style="${blockStyle}">
  			  <div class="spixn-buyback-modal-core-content" style="${contentStyle}">

				<div class="spixn-buyback-modal-core-title"
					style="background: ${SPIXN_BUTTON_ACTIVE_BACKGROUND}; color: #ffffff; padding: ${calcSize('0.3rem')}; border-radius: 6px; font-size: ${calcSize('0.9rem')}; margin: ${calcSize('-2rem')} auto ${calcSize('1rem')}; text-align: center; min-width: ${calcSize('12rem')}; max-width: 400px">
					保証期間
					<strong>${insurancePeriodYear}年間</strong>
					内であればいつでも利用可能
				</div>
				<p style="margin-bottom: ${calcSize('1.2rem')}; text-align: left;font-size: ${calcSize('0.85rem')};">受取時の状態（A・B・C）を評価し、該当する保証金額をお支払い。Cに至らない品質の場合は再評価し、適正額をお支払いします。</p>
				${showPriceRangeConfig ? priceRangeTitleHTML : ''}
				<ul style="list-style: none; padding: 0; margin: 0;display:flex;flex-direction:column;gap:${calcSize('5px')};">
					${['A', 'B', 'C'].map((grade, idx) => {
						const labels = ['未使用に近い', 'ほとんどキズがない', 'ややキズや汚れがある'];
						const descriptions = ['数回しか使用しておらず、傷や汚れがない', 'よく見ないとわからない程度の傷や汚れがある', '目立たない傷や汚れがある'];
						const prices = [plan.a_price, plan.b_price, plan.c_price];
						const price_ratios = [plan.a_price_ratio, plan.b_price_ratio, plan.c_price_ratio];
						const percentages = price_ratios.map(p => Math.round(p * 100));
						const priceToDisplay = showPriceAsPercentageConfig ? percentages[idx] : prices[idx].toLocaleString();
						const currentPricePrefix = showPriceAsPercentageConfig ? '' : '￥';
						const currentPriceUnit = showPriceAsPercentageConfig ? '%' : '';
						const rangesHTML = [`￥${plan.a_min_price.toLocaleString()} 〜 ￥${plan.a_max_price.toLocaleString()}`, `￥${plan.b_min_price.toLocaleString()} 〜 ￥${plan.b_max_price.toLocaleString()}`, `￥${plan.c_min_price.toLocaleString()} 〜 ￥${plan.c_max_price.toLocaleString()}`].map(r => `<span style="font-size: ${calcSize('0.8rem')};color:#A3A3A3">${r}</span>`)[idx];
						return `
						<li style="display: flex; 
								   flex-wrap: nowrap;
								   justify-content: space-between;
								   align-items: center; ">
						  <div style="display: flex; align-items: center;">
						    <div style="
							  background: ${SPIXN_BASE_COLOR};
							  color: white;
							  font-weight: bold;
							  border-radius: 50%;
							  width: ${calcSize('2rem')};
							  height: ${calcSize('2rem')};
							  min-width: ${calcSize('2rem')};
							  min-height: ${calcSize('2rem')};
							  text-align: center;
							  line-height: ${calcSize('2rem')};
							  margin-right: ${calcSize('0.8rem')};">
							  ${grade}
							</div>
						    <div style="display: flex; flex-direction: column;">
							  <span style="font-weight: bold; text-align: left;font-size: ${calcSize('0.9rem')};">
							    ${labels[idx]}
							  </span>
							  <span style="font-size: ${calcSize('0.75rem')}; text-align: left;">${descriptions[idx]}</span>
							</div>
						  </div>
						  <div
						    style="
							  display:flex;
							  flex-direction:column;
							  align-items:end;
							  color: ${SPIXN_BASE_COLOR};
							  font-size: ${calcSize('1.5rem')};
							  white-space: nowrap;
							  font-weight: 500;">
							<p style="
							  display:flex;
							  align-items: baseline;
							  line-height: normal;
							  margin:0;">
							  <span style="font-size: ${calcSize('0.8rem')};">
							    ${currentPricePrefix}
							  </span>
							  ${priceToDisplay}
							  <span style="font-size: ${calcSize('0.8rem')};">
							    ${currentPriceUnit}
							  </span>
						    </p>
							${showPriceRangeConfig ? rangesHTML : ''}
						  </div>
						</li>`;
					}).join('')}
				</ul>
				<div style="font-size: ${calcSize('0.85rem')}; text-align: left; margin-top: ${calcSize('1rem')};">
					<a href="/pages/spixn" target="_blank" style="color: ${SPIXN_BASE_COLOR}; text-decoration: underline;">
						バイバック保証について更に詳しくみる
					</a>
				</div>

			  </div>	
			</div>
		`;
		
		const toggleTitle = part.querySelector('.spixn-buyback-info-part-title');
		const toggleButton = part.querySelector('.spixn-buyback-info-part-chevron');
		const modalCore = part.querySelector('.spixn-buyback-modal-core');
		
		if (toggleTitle && modalCore && toggleButton) {
			let accordionState = true;
			const toggleAccordionState = e => {
				e?.preventDefault();
				accordionState = !accordionState;
				modalCore.style.maxHeight = accordionState ? ACCORDION_MAX_HEIGHT : '0';
				modalCore.style.backgroundColor = accordionState ? SPIXN_BUTTON_ACTIVE_BACKGROUND : 'transparent';

				modalCore.style.overflow = accordionState ? 'visible' : 'hidden';
				modalCore.style.padding = accordionState ? '4px' : `0 4px`;
				modalCore.style.margin = accordionState ? `${calcSize('20px')} 0 ${calcSize('1rem')}` : '0';
				
				toggleButton.style.transform = accordionState
					? 'rotate(180deg) translateY(-8px)'
					: 'rotate(0deg)';
			};
			toggleTitle.addEventListener('click', toggleAccordionState);
			toggleAccordionState(); // 閉じた状態が初期状態
		} else {
			log("❌ Error: Accordion elements not found in createBuybackInfoPart");
		}
		
		return part;
	}

	// 統合バリデーション設定（オリジナルから追加）
	setupUnifiedValidation() {
		log('🔄 Setting up unified validation (Mini-Cart版)');
		
		if (this.periodicValidationInterval) {
			clearInterval(this.periodicValidationInterval);
		}
		
		this.periodicValidationInterval = setInterval(() => {
			this.performUnifiedValidationAndCleanup();
		}, 1000);
		
		log('✅ Unified validation setup complete (1秒間隔)');
	}

	// 統合バリデーション・削除処理（オリジナルから追加）
	async performUnifiedValidationAndCleanup(isInitialLoad = false) {
		try {
			if (this.isProcessing || this.pendingDeletions.size > 0) {
				log('⏸️ Unified validation paused during processing');
				return;
			}

			const orphanedProducts = [];
			const validationAlerts = [];

			// APIを使った孤立バイバック商品の検知
			await this.detectOrphanedBuybackProductsByAPI(orphanedProducts);

			// 全ての関係について統合チェック（メイン商品とバイバック商品の両方）
			for (const [variantId, relation] of this.buybackRelations) {
				if (relation.type === 'main') {
					const mainQuantity = this.getVariantQuantityFromDOM(variantId);
					const buybackQuantity = this.getVariantQuantityFromDOM(relation.buybackVariantId);
					
					// バイバック商品が存在する場合のみチェック
					if (buybackQuantity > 0) {
						if (mainQuantity === 0) {
							// ケース1: メイン商品が0 → 孤立商品として削除対象
							const isDuplicate = orphanedProducts.some(orphan => 
								orphan.buybackVariantId === relation.buybackVariantId);
							if (!isDuplicate) {
								orphanedProducts.push({
									buybackVariantId: relation.buybackVariantId,
									mainVariantId: variantId,
									quantity: buybackQuantity,
									detectedBy: 'relation_check'
								});
								log('🚨 統合チェック: 孤立バイバック商品発見（メイン商品0）', {
									buybackVariantId: relation.buybackVariantId,
									mainVariantId: variantId,
									mainQuantity,
									buybackQuantity
								});
							}
						}
					}
				} else if (relation.type === 'buyback') {
					// バイバック商品の孤立チェック
					const buybackQuantity = this.getVariantQuantityFromDOM(variantId);
					const mainQuantity = this.getVariantQuantityFromDOM(relation.mainVariantId);
					
					// バイバック商品が存在し、対応するメイン商品が存在しない場合
					if (buybackQuantity > 0 && mainQuantity === 0) {
						const isDuplicate = orphanedProducts.some(orphan => 
							orphan.buybackVariantId === variantId);
						if (!isDuplicate) {
							orphanedProducts.push({
								buybackVariantId: variantId,
								mainVariantId: relation.mainVariantId,
								quantity: buybackQuantity,
								detectedBy: 'relation_check'
							});
							log('🚨 統合チェック: 孤立バイバック商品発見（メイン商品不在）', {
								buybackVariantId: variantId,
								mainVariantId: relation.mainVariantId,
								mainQuantity,
								buybackQuantity
							});
						}
					}
				}
			}

			// 孤立商品の削除処理（優先実行）
			if (orphanedProducts.length > 0) {
				await this.handleOrphanedBuybackProducts(orphanedProducts, isInitialLoad);
				return;
			}

		} catch (error) {
			console.error('❌ Error in unified validation and cleanup:', error);
		}
	}

	// mini-cart専用のDOM数量取得
	getVariantQuantityFromDOM(variantId) {
		const items = document.querySelectorAll(`${CART_CONTAINER_SELECTOR} ${ITEM_SELECTOR}[data-variant-id="${variantId}"]`);
		for (const item of items) {
			const input = item.querySelector(INPUT_SELECTOR);
			if (input) {
				return parseInt(input.value, 10) || 0;
			}
		}
		return 0;
	}

	// 孤立バイバック商品の削除処理
	async handleOrphanedBuybackProducts(orphanedProducts, isInitialLoad = false) {
		log('🗑️ 統合処理: 孤立バイバック商品の削除開始', {
			orphanedProducts,
			isInitialLoad
		});
		
		// 削除処理中フラグを設定
		orphanedProducts.forEach(orphan => {
			this.pendingDeletions.add(orphan.buybackVariantId);
		});

		// バリデーションを一時停止
		const wasValidationRunning = this.periodicValidationInterval !== null;
		if (wasValidationRunning) {
			clearInterval(this.periodicValidationInterval);
			this.periodicValidationInterval = null;
		}

		try {
			// 削除APIを順次実行
			for (const orphan of orphanedProducts) {
				try {
					await this.removeFromCart(orphan.buybackVariantId);
					log('✅ 孤立バイバック商品削除成功:', orphan.buybackVariantId);
				} catch (error) {
					console.error('❌ 孤立バイバック商品削除失敗:', orphan.buybackVariantId, error);
				}
			}
			
			await new Promise(resolve => setTimeout(resolve, 100));
			
			const message = isInitialLoad
				? `対象商品がカート上にないため、バイバック保証を削除します。画面を更新します。`
				: `対象商品が削除されたため、対応するバイバック保証も削除します。画面を更新します。`;
			
			alert(message);
			window.location.reload();
			
		} finally {
			// 削除処理中フラグをクリア
			orphanedProducts.forEach(orphan => {
				this.pendingDeletions.delete(orphan.buybackVariantId);
			});
			
			// バリデーションを再開
			if (isInitialLoad && wasValidationRunning && this.periodicValidationInterval === null) {
				this.setupUnifiedValidation();
			}
		}
	}

	// カートの数量を更新
	async updateCartQuantity(variantId, quantity) {
		log('🔄 Updating cart quantity for variant', variantId, 'to', quantity);
		
		const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ id: variantId.toString(), quantity: quantity })
		});

		if (!response.ok) {
			throw new Error(`Failed to update cart quantity: ${response.status}`);
		}

		return response.json();
	}

	// カートから商品を削除
	async removeFromCart(variantId) {
		log('🗑️ Removing variant', variantId, 'from cart');
		return this.updateCartQuantity(variantId, 0);
	}

	// APIを使った孤立バイバック商品の検知（オリジナルから追加）
	async detectOrphanedBuybackProductsByAPI(orphanedProducts) {
		try {
			// カート内の全商品のproduct IDを取得
			const productIds = this.getCartProductIds();
			if (productIds.length === 0) {
				log('📦 No products in cart for API buyback check');
				return;
			}

			log('🔍 APIによる孤立バイバック商品検知開始', {
				totalProducts: productIds.length,
				productIds: productIds
			});

			// APIでバイバック保証商品リストを取得
			const buybackProductIds = await getBuybackProductList(productIds);
			if (buybackProductIds.length === 0) {
				log('📦 No buyback products found in cart via API');
				return;
			}

			log('🔍 APIでバイバック保証商品を検出', {
				buybackProductIds: buybackProductIds,
				count: buybackProductIds.length
			});

			// バイバック保証商品について対象商品が存在するかチェック
			for (const buybackProductId of buybackProductIds) {
				const buybackVariants = this.getVariantsByProductId(buybackProductId);
				
				for (const buybackVariantId of buybackVariants) {
					const buybackQuantity = this.getVariantQuantityFromDOM(buybackVariantId);
					
					// バイバック商品が実際にカートに存在する場合のみチェック
					if (buybackQuantity > 0) {
						// このバイバック商品に対応する対象商品を検索
						const hasCorrespondingMainProduct = await this.hasCorrespondingMainProduct(buybackVariantId);
						
						if (!hasCorrespondingMainProduct) {
							// 対象商品が存在しない孤立バイバック商品
							orphanedProducts.push({
								buybackVariantId: buybackVariantId,
								mainVariantId: null,
								quantity: buybackQuantity,
								detectedBy: 'api_check'
							});
							
							log('🚨 APIチェック: 孤立バイバック商品発見（対象商品なし）', {
								buybackVariantId: buybackVariantId,
								buybackProductId: buybackProductId,
								quantity: buybackQuantity
							});
						}
					}
				}
			}

			log('✅ APIによる孤立バイバック商品検知完了', {
				detectedOrphans: orphanedProducts.filter(p => p.detectedBy === 'api_check').length
			});

		} catch (error) {
			console.error('❌ Error in API-based orphaned buyback product detection:', error);
		}
	}

	// カート内の商品のproduct IDリストを取得
	getCartProductIds() {
		const productIds = [];
		for (const [variantId, cartItem] of this.cartState) {
			if (cartItem.quantity > 0 && cartItem.product_id) {
				const productId = parseInt(cartItem.product_id, 10);
				if (!productIds.includes(productId)) {
					productIds.push(productId);
				}
			}
		}
		return productIds;
	}

	// 指定されたproduct IDに属するvariantのリストを取得
	getVariantsByProductId(productId) {
		const variants = [];
		for (const [variantId, cartItem] of this.cartState) {
			if (parseInt(cartItem.product_id, 10) === productId) {
				variants.push(variantId);
			}
		}
		return variants;
	}

	// 指定されたバイバック商品に対応する対象商品が存在するかチェック
	async hasCorrespondingMainProduct(buybackVariantId) {
		try {
			// 既存のbuybackRelationsから対応するメイン商品を検索
			const relation = this.buybackRelations.get(buybackVariantId);
			if (relation && relation.type === 'buyback') {
				const mainQuantity = this.getVariantQuantityFromDOM(relation.mainVariantId);
				return mainQuantity > 0;
			}

			// buybackRelationsにない場合は、逆引きでメイン商品を検索
			for (const [variantId, cartItem] of this.cartState) {
				if (cartItem.quantity > 0) {
					try {
						const buybackData = await getBuybackInformation(variantId);
						if (buybackData && buybackData.buyback_variant_id === buybackVariantId) {
							log('🔍 逆引きで対象商品発見', {
								mainVariantId: variantId,
								buybackVariantId: buybackVariantId
							});
							return true;
						}
					} catch (error) {
						// 個別のエラーは無視して続行
						log('⚠️ Error checking buyback information for variant', variantId, error);
					}
				}
			}

			return false;
		} catch (error) {
			console.error('❌ Error checking corresponding main product:', error);
			return false;
		}
	}

	// シェブロンアイコンを作成（オリジナルと完全に同じ）
	createChevronIcon() {
		const ret = document.createElement('div');
		ret.style.cssText = `
			display: flex; 
			width: 21px; 
			height: 20px; 
			flex-direction: column; 
			align-items: end; 
			transition: transform 0.2s ease-in-out;
		`;
		ret.className = 'spixn-buyback-info-part-chevron';
		ret.innerHTML = `<svg style="margin-top: 8px;" width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M3.30806 7.25923C3.55214 7.01515 3.94786 7.01515 4.19194 7.25923L10 13.0673L15.8081 7.25923C16.0521 7.01515 16.4479 7.01515 16.6919 7.25923C16.936 7.50331 16.936 7.89904 16.6919 8.14311L10.4419 14.3931C10.1979 14.6372 9.80214 14.6372 9.55806 14.3931L3.30806 8.14311C3.06398 7.89904 3.06398 7.50331 3.30806 7.25923Z" fill="#3C2238"/></svg>`;
		return ret.outerHTML;
	}

	createSpixnMarkSVG(width) {
		const height = width ? (width * 62) / 70 : 62;
		return `
<svg width="${width}" height="${height}" viewBox="0 0 70 62" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M5.26193 55.2211C9.24219 59.2039 14.6339 61.4544 20.2646 61.4831H20.5006C26.3899 61.4831 32.0989 58.8987 36.6525 54.1898L37.0377 53.755L39.417 51.0837L54.4693 61.5825H70L49.6983 47.4123L21.2275 27.533L14.4312 22.793C13.5643 22.1302 12.8571 21.2813 12.3618 20.3089C11.8666 19.3365 11.5958 18.2653 11.5696 17.1743C11.5434 16.0833 11.7623 15.0004 12.2103 14.0053C12.6582 13.0102 13.3238 12.1283 14.1579 11.4246C16.0556 9.7475 18.5087 8.83533 21.0411 8.86512H21.0784C26.3029 8.86512 30.5645 12.4931 30.5832 16.9411C30.5932 17.446 30.5453 17.9504 30.4403 18.4444L30.2539 19.1092C30.1066 19.5677 29.9152 20.0108 29.6824 20.4324C29.4052 20.9315 29.0762 21.4 28.7008 21.8301L27.3341 23.4143L34.6708 28.5332L35.4101 27.6386C36.9154 25.9081 38.0488 23.8866 38.7398 21.6997L39.0132 20.7119L39.0753 20.4821C39.3322 19.3257 39.4593 18.1443 39.4543 16.9597C39.417 7.59782 31.1671 0.000204725 21.0535 0.000204725H20.9852C16.2545 -0.0216784 11.6837 1.71145 8.15685 4.86441C6.42436 6.39397 5.03705 8.27456 4.08702 10.3813C3.13699 12.4881 2.64597 14.7729 2.64657 17.084C2.67782 19.5402 3.25673 21.9586 4.3411 24.1628C5.42547 26.367 6.98798 28.3015 8.91475 29.8253L9.03898 29.9309L32.0679 45.9834L30.4278 47.8471C28.3467 50.0587 24.9114 52.643 20.3515 52.6181C17.0947 52.6094 13.9716 51.3222 11.6544 49.0337C10.7874 48.1758 10.0997 47.1539 9.63153 46.0276C9.16331 44.9014 8.92388 43.6932 8.92717 42.4735V37.7832H0.000134292V42.399C-0.00903067 44.7772 0.450997 47.1339 1.35389 49.3341C2.25679 51.5343 3.58482 53.5348 5.26193 55.2211Z" fill="${SPIXN_BASE_COLOR}"/>
<path d="M51.5235 24.1475L42.6523 34.1057L50.0015 39.2309L63.4386 24.1475H51.5235Z" fill="${SPIXN_BASE_COLOR}"/>
<path d="M62.7432 18.6621C65.9236 18.6621 68.5019 16.0838 68.5019 12.9033C68.5019 9.72282 65.9236 7.14453 62.7432 7.14453C59.5627 7.14453 56.9844 9.72282 56.9844 12.9033C56.9844 16.0838 59.5627 18.6621 62.7432 18.6621Z" fill="#1AB298"/>
</svg>
		`;
	}

	createSpixnMarkCircleSVG(useGreenMark=true) {
		const color = useGreenMark ? SPIXN_GREEN_FOR_MARK : SPIXN_BASE_COLOR;
		return `
			<svg width="14" height="13" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg" style="vertical-align: sub;">
			<g clip-path="url(#clip0_1427_3087)">
			<path d="M7.00123 13.0025C10.5918 13.0025 13.5025 10.0918 13.5025 6.50123C13.5025 2.9107 10.5918 0 7.00123 0C3.4107 0 0.5 2.9107 0.5 6.50123C0.5 10.0918 3.4107 13.0025 7.00123 13.0025Z" fill="${color}"/>
			<path d="M3.96566 9.09757C4.42183 9.55602 5.04143 9.81448 5.68816 9.8161H5.71277C6.06158 9.80932 6.40539 9.73191 6.72344 9.58853C7.04149 9.44516 7.32717 9.2388 7.56323 8.98192L7.60753 8.9327L7.87575 8.63496L9.59825 9.83579H11.3774L9.04951 8.21171L5.78905 5.92816L5.01146 5.3868C4.9124 5.31079 4.83161 5.21357 4.77501 5.10227C4.71841 4.99097 4.68744 4.86841 4.68437 4.74359C4.6813 4.61876 4.70622 4.49483 4.75728 4.38088C4.80834 4.26694 4.88426 4.16587 4.97947 4.08508C5.19671 3.89346 5.47725 3.78914 5.76691 3.79226C6.36486 3.79226 6.85209 4.20812 6.85455 4.71995C6.85491 4.77537 6.84913 4.83067 6.83732 4.88482L6.81763 4.9611C6.79964 5.01287 6.77742 5.06307 6.7512 5.1112C6.71941 5.16848 6.68146 5.22212 6.638 5.27114L6.48298 5.45324L7.32209 6.0389L7.41067 5.93801C7.49933 5.83491 7.57687 5.72273 7.64198 5.60334C7.70324 5.4932 7.75273 5.3769 7.78962 5.25638L7.82161 5.14565V5.11858C7.85105 4.98608 7.8659 4.85076 7.8659 4.71503C7.8659 3.64215 6.91606 2.77351 5.75952 2.77351C5.21493 2.76899 4.68831 2.96823 4.28309 3.3321C4.08469 3.50684 3.92586 3.7219 3.81722 3.96293C3.70859 4.20396 3.65265 4.46541 3.65315 4.72979C3.65661 5.01058 3.7227 5.28706 3.84658 5.53908C3.97046 5.79109 4.14901 6.01228 4.36922 6.18653L4.38398 6.19884L7.01941 8.037L6.8324 8.24617C6.68824 8.41205 6.51126 8.54624 6.31262 8.64027C6.11398 8.73429 5.898 8.78611 5.67832 8.79245C5.30467 8.79231 4.94597 8.64565 4.67926 8.38397C4.48976 8.18669 4.38319 7.92421 4.38152 7.65067V7.11177H3.36524V7.64082C3.36468 7.91118 3.41743 8.179 3.52045 8.42896C3.62348 8.67892 3.77476 8.90612 3.96566 9.09757Z" fill="white"/>
			<path d="M9.25796 5.54199L8.24414 6.68131L9.08325 7.26942L10.6212 5.54199H9.25796Z" fill="white"/>
			<path d="M10.5423 4.91367C10.9065 4.91367 11.2018 4.61842 11.2018 4.2542C11.2018 3.88998 10.9065 3.59473 10.5423 3.59473C10.1781 3.59473 9.88281 3.88998 9.88281 4.2542C9.88281 4.61842 10.1781 4.91367 10.5423 4.91367Z" fill="white"/>
			</g>
			<defs>
			<clipPath id="clip0_1427_3087">
			<rect width="13" height="13" fill="white" transform="translate(0.5)"/>
			</clipPath>
			</defs>
			</svg>
		`;
	}
}

async function initialize(shopId, apiBaseUrl, options = {}) {
	log('Initializing Shopify Buyback Mini-Cart...');
	if (typeof options.debugMode !== 'undefined') {
		DEBUG_MODE = options.debugMode;
		log = DEBUG_MODE ? console.log : () => {};
	}
	
	log('🚀 Shopify Mini-Cart Buyback Display initialized', { debugMode: DEBUG_MODE });
	
	if (apiBaseUrl) {
		API_BASE_URL = apiBaseUrl;
	}
	
	if (shopId && validateShopId(shopId)) {
		setShopId(shopId);
	} else {
		console.warn('[SPIXN Mini-Cart] Shop ID not valid:', shopId);
		return;
	}
	
	const cartElement = document.querySelector(CART_CONTAINER_SELECTOR);
	if (cartElement) {
		log('🛒 Mini-cart container found, setting up BuybackMiniCartManager');
		new BuybackMiniCartManager();
		log('✅ BuybackMiniCartManager setup complete');
	} else {
		log('⚠️ Mini-cart container not found, skipping setup');
	}
}

window.ShopifyBuybackMiniCart = {
	initialize,
	getBuybackInformation,
	getShopId,
	setShopId
};

})();
